import React, { useState } from 'react';

import { Button, Modal, MultiSelect, Space } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { ResourceService } from 'services/resource-service';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { Resource } from 'shared/interfaces/Resource';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';

interface TagEditModalProps {
  opened: boolean;
  onClose: () => void;
  tags: string[];
  resource?: Resource;
  smartAssistant?: SmartAssistant;
  onEditDone?: () => void;
}

export function TagEditModal({ opened, onClose, tags, resource, smartAssistant, onEditDone }: TagEditModalProps) {
  const { t } = useTranslation();
  const [inputCreateValue, setInputCreateValue] = useState('');

  const tagsForm = useForm({
    initialValues: { tags: resource?.tags ?? smartAssistant?.tags ?? [] }
  });

  const smartAssistantTagEditMutation = useMutation({
    mutationFn: () =>
      SmartAssistantService.updateSmartAssistant({ id: smartAssistant?._id ?? '', tags: tagsForm.values.tags }),
    onSuccess: () => NotificationService.showResult(true, t('FEED_BACK.TAG.UPDATE.SUCCESS')),
    onError: () => NotificationService.showResult(false, t('FEED_BACK.TAG.UPDATE.FAILURE')),
    onSettled: () => {
      if (onEditDone) onEditDone();
      onClose();
    }
  });

  const resourceTagEditMutation = useMutation({
    mutationFn: () =>
      ResourceService.updateResourceInfo({ resourceId: resource?._id ?? '', tags: tagsForm.values.tags }),
    onSuccess: () => NotificationService.showResult(true, t('FEED_BACK.TAG.UPDATE.SUCCESS')),
    onError: () => NotificationService.showResult(false, t('FEED_BACK.TAG.UPDATE.FAILURE')),
    onSettled: () => {
      if (onEditDone) onEditDone();
      onClose();
    }
  });

  const onSaveClick = () => {
    const tagCreationFormValidation = tagsForm.validate();
    if (tagCreationFormValidation.hasErrors) return;
    if (resource) resourceTagEditMutation.mutate();
    if (smartAssistant) smartAssistantTagEditMutation.mutate();
  };

  return (
    <Modal padding="md" opened={opened} withCloseButton onClose={onClose} title={t('TAG.EDIT.TITLE')}>
      <MultiSelect
        searchable
        creatable
        limit={20}
        withinPortal
        data={tags.map((tag) => ({ value: tag, label: tag }))}
        placeholder={t('TAG.EDIT.PLACE_HOLDER')}
        getCreateLabel={(newTag) => `+ ${t('APP.ACTIONS.CREATE')} ${newTag}`}
        onCreate={(newTag) => {
          tagsForm.setFieldValue('tags', [...tagsForm.values.tags, newTag]);
          tags.push(newTag);
          return { value: newTag, label: newTag };
        }}
        error={tagsForm.errors.tags}
        value={tagsForm.values.tags}
        onChange={(value) => tagsForm.setFieldValue('tags', value)}
        searchValue={inputCreateValue}
        onSearchChange={(value) => setInputCreateValue(value)}
        onKeyUp={(event) => {
          if (event.nativeEvent.code === 'Enter') {
            event.preventDefault();
            tagsForm.setFieldValue('tags', [...tagsForm.values.tags, inputCreateValue]);
            tags.push(inputCreateValue);
            setInputCreateValue('');
          }
        }}
      />
      <Space h="xs" />
      <Button
        my="xs"
        style={{ float: 'right' }}
        loading={resourceTagEditMutation.isLoading || smartAssistantTagEditMutation.isLoading}
        leftIcon={<IconDeviceFloppy />}
        onClick={onSaveClick}>
        {t('APP.ACTIONS.SAVE')}
      </Button>
    </Modal>
  );
}
